<script>
import Layout from '@layouts/main.vue'
import baseMixin from '@/mixins/baseMixin'
import idleMixin from '@/mixins/idleMixin'
import { mapActions, mapGetters } from 'vuex'
import BookingCreateMeta from '@/components/booking/booking-create-meta.vue'
import BookingLocationList from '@/components/booking/booking-location-list.vue'
import { isEmpty } from 'lodash'
import PromiseDialog from '@/components/general/promise-dialog.vue'
import BookingCreateSummaryCard from '@/components/booking/booking-create-summary-card.vue'
import dayjs from '@/services/date'

export default {
  name: 'BookingCreateView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Booking Create page.' }],
    }
  },
  components: {
    Layout,
    BookingCreateMeta,
    BookingLocationList,
    BookingCreateSummaryCard,
    PromiseDialog,
  },
  mixins: [baseMixin, idleMixin],
  data: () => {
    return {
      selectedLocation: null,
      formChanged: false,
      showSummaryPage: false,
      summaryData: null,
      preselectDate: null,
    }
  },
  computed: {
    ...mapGetters({
      locations: 'locations/bookingLocations',
    }),
  },
  watch: {
    idleMixin_isIdle(v, o) {
      this.loadLocationBookingBlocks()
    },
  },
  async created() {
    this.validatePreselectDate()

    this.loadLocationBookingBlocks()
    const result = await this.loadBookingLocations()

    // Auto select location if only one was returned
    if (this.hasSingleLocation(result)) this.selectLocation(result.data[0])
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.formChanged) {
      next()
    } else {
      // Wait for users response to dialog
      const result = await this.$refs.unsavedChangesDialog.show({
        text: this.$t('general.discardDialogBodyText'),
        title: this.$t('general.discardDialogTitle'),
        actions: {
          cancel: this.$t('general.cancelBtnText'),
          confirm: {
            text: this.$t('general.discardDialogDiscardBtnText'),
            color: 'red',
          },
        },
        icon: false,
        persistant: true,
      })

      if (result === 'ok') next()
    }
  },
  methods: {
    ...mapActions({
      loadBookingLocations: 'locations/loadBookingLocations',
      loadLocationBookingBlocks: 'locations/loadLocationBookingBlocks',
      setRecentLocation: 'bookings/setRecentLocation',
    }),
    validatePreselectDate() {
      if (
        !this.$route.query ||
        !Object.prototype.hasOwnProperty.call(this.$route.query, 'date')
      )
        return

      const dateQuery = dayjs(this.$route.query.date)
      const isPast = dateQuery.isBefore(dayjs())

      if (isPast) return
      this.preselectDate = this.$route.query.date
    },
    selectLocation(location) {
      this.selectedLocation = location
      this.setRecentLocation(location.locationId)
    },
    changeLocation() {
      this.selectedLocation = null
    },
    hasSingleLocation(resultObj) {
      return (
        resultObj.isSuccess &&
        !isEmpty(resultObj.data) &&
        Array.isArray(resultObj.data) &&
        resultObj.data.length === 1
      )
    },
    handleDisplaySummaryPage(summaryObj) {
      this.formChanged = false
      this.summaryData = summaryObj
      this.showSummaryPage = true
    },
    createNewBooking() {
      this.showSummaryPage = false
      if (this.hasSingleLocation({ isSuccess: true, data: this.locations }))
        this.selectLocation(this.locations[0])
      else this.changeLocation()
      this.summaryData = null
    },
  },
}
</script>

<template>
  <Layout>
    <BookingCreateSummaryCard
      v-if="showSummaryPage"
      :summary-data="summaryData"
      @createNewBooking="createNewBooking"
    />
    <BookingLocationList
      v-else-if="!selectedLocation"
      @selectLocation="selectLocation"
    />
    <BookingCreateMeta
      v-else
      :selected-location="selectedLocation"
      :preselect-date="preselectDate"
      @showSummaryPage="handleDisplaySummaryPage"
      @changeLocation="changeLocation"
      @updated="formChanged = true"
    />
    <PromiseDialog ref="unsavedChangesDialog" />
  </Layout>
</template>
